import { ChoiceChips, PageHeader } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { LoadMore } from '../LoadMore.tsx';
import { usePageContext } from '../usePageContext.ts';
import { LIST_PAGE_SIZE } from '../useKeysetPages.ts';
import { NotificationList, useChooseNotification } from './NotificationList.tsx';
import styles from './Notifications.module.css';
import { MarkAllRead, useFilterChoices } from './NotificationsPanel.tsx';
import { useNotificationPages, useInboxCounts, type NotificationFilter } from './useNotifications.ts';

export type NotificationsPageProps = {
  api: TypedClient;
  filter: NotificationFilter;
  onFilterChange: (filter: NotificationFilter) => void;
};

const keepPage = (): void => undefined;

export const NotificationsPage = ({ api, filter, onFilterChange }: NotificationsPageProps) => {
  const { t, locale, describeError } = usePageContext();
  const { unread } = useInboxCounts(api);
  const pages = useNotificationPages(api, filter, LIST_PAGE_SIZE);
  const choose = useChooseNotification(api, keepPage);
  const choices = useFilterChoices(unread);

  return (
    <div className={styles['page']}>
      <PageHeader
        title={t('notifications.title')}
        context={t('notifications.pageContext')}
        action={unread > 0 ? <MarkAllRead api={api} newestId={pages.items[0]?.id} /> : undefined}
      />
      <ChoiceChips
        legend={t('notifications.filterLegend')}
        hideLegend
        choices={choices}
        value={filter}
        onChange={onFilterChange}
        locale={locale}
      />
      <div className={styles['card']}>
        <NotificationList
          filter={filter}
          loading={pages.query.isPending}
          error={pages.query.isError ? describeError(pages.query.error) : undefined}
          onRetry={() => void pages.query.refetch()}
          items={pages.items}
          onChoose={choose}
        />
      </div>
      {pages.items.length === 0 ? null : (
        <LoadMore
          shownLabel={t('common.shown', { shown: pages.items.length, total: pages.total })}
          hasMore={pages.hasMore}
          loading={pages.loadingMore}
          onLoadMore={pages.loadMore}
        />
      )}
    </div>
  );
};
